import React, { createContext, CSSProperties, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

type Position = 'start' | 'end' | 'bottom';
interface Props {
  children?: React.ReactNode;
  position?: Position;
  zIndex?: CSSProperties['zIndex'];
}

export const contentPanelStartId = 'content-panel-start';
export const contentPanelEndId = 'content-panel-end';

export const PanelPortalContext = createContext<{
  hasPanel: boolean;
  setHasPanel: (hasPanel: boolean) => void;
  position?: Position;
  setPosition: (position: Position) => void;
  zIndex?: CSSProperties['zIndex'];
  setZIndex: (zIndex: CSSProperties['zIndex']) => void;
}>({
  hasPanel: false,
  setHasPanel: () => null,
  position: 'end',
  setPosition: () => null,
  zIndex: null,
  setZIndex: () => null
});

export function PanelPortalProvider({ children }: { children: React.ReactNode }) {
  const [hasPanel, setHasPanel] = useState(false);
  const [position, setPosition] = useState<Position>('end');
  const [zIndex, setZIndex] = useState<CSSProperties['zIndex']>(null);
  const value = useMemo(
    () => ({
      hasPanel,
      setHasPanel,
      position,
      setPosition,
      zIndex,
      setZIndex
    }),
    [hasPanel, position, zIndex]
  );

  return <PanelPortalContext.Provider value={value}>{children}</PanelPortalContext.Provider>;
}

/**
 * For use at the top level of a page component for optionally displaying panel content
 *
 * Renders children into a context so that layout can be handled by AppLayout
 * Page MUST be using AppPageLayout / AppLayout that includes the provider
 */
export default function AppPanelPortal({ children, position = 'end', zIndex = null }: Props): JSX.Element {
  const {
    hasPanel,
    setHasPanel,
    position: currentPosition,
    setPosition,
    zIndex: currentZIndex,
    setZIndex
  } = useContext(PanelPortalContext);

  const hasChildren = children != null;
  useEffect(() => {
    if (hasChildren !== hasPanel) {
      setHasPanel(hasChildren);
    }
  }, [hasChildren, hasPanel, setHasPanel]);

  if (position !== currentPosition) {
    setPosition(position);
  }
  if (zIndex !== currentZIndex) {
    setZIndex(zIndex);
  }

  useEffect(
    () => () => {
      setHasPanel(false);
    },
    [setHasPanel]
  );

  const panelElement = document.getElementById(
    position === 'end' || position === 'bottom' ? contentPanelEndId : contentPanelStartId
  );
  if (hasPanel && panelElement) {
    return createPortal(children, panelElement);
  }

  return null;
}
